import { Injectable } from '@angular/core';
import { GridColumnSettings } from '@common/models/User.model';
import { currentUserSubject, UserService } from '@common/services/user.service';
import { Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { environment } from '../../../environments/environment';

const SAVE_DEBOUNCE_MS = 1500;
const STORAGE_PREFIX = 'pcs.grid.pending';
/** Key prefix of the old read-through cache, which took priority over the DB - purged on startup. */
const LEGACY_STORAGE_PREFIX = 'pcs.grid.columns';

/**
 * Per-grid Kendo column layout (visibility + column order), persisted per user in the DB
 * (CurrentUserDisplayModel.gridSettings, re-fetched on every page load), which is the source of
 * truth - so a layout saved on one browser/device shows up on the others after a reload.
 *
 * localStorage is only a write-ahead buffer for edits that haven't been confirmed saved yet (tab
 * closed inside the debounce window, backend unreachable): written synchronously on every change,
 * removed once the backend confirms. A leftover entry is applied and re-sent on the next load.
 * Writes are debounced and best-effort flushed on unload.
 */
@Injectable({ providedIn: 'root' })
export class GridColumnSettingsService {
    private saveTriggers = new Map<string, Subject<void>>();
    /** Settings not yet confirmed sent to the backend, per gridKey - used by flushPending() */
    private dirty = new Map<string, GridColumnSettings>();

    constructor(private userService: UserService) {
        this.removeLegacyCache();
        window.addEventListener('beforeunload', () => this.flushPending());
        window.addEventListener('visibilitychange', () => {
            if (document.visibilityState === 'hidden') {
                this.flushPending();
            }
        });
    }

    getVisibility(
        gridKey: string,
        columnKeys: string[],
        defaultVisibility: Record<string, boolean> = {}
    ): Record<string, boolean> {
        const defaults = this.getDefaults(columnKeys, defaultVisibility);
        const saved = this.resolveSaved(gridKey);
        return saved ? this.mergeKnownSettings(columnKeys, defaults, saved.visibility) : defaults;
    }

    /** Saved column order (column keys); empty = the order declared in the template. */
    getOrder(gridKey: string): string[] {
        return this.resolveSaved(gridKey)?.order ?? [];
    }

    setColumnVisibility(
        gridKey: string,
        columnKey: string,
        visible: boolean,
        current: Record<string, boolean>,
        defaults: Record<string, boolean>
    ) {
        const updated = { ...current, [columnKey]: visible };
        this.persist(gridKey, { visibility: this.diffFromDefaults(updated, defaults), order: this.getOrder(gridKey) });
    }

    setAllColumnsVisibility(gridKey: string, columnKeys: string[], visible: boolean, defaults: Record<string, boolean>) {
        const updated = columnKeys.reduce(
            (acc, key) => {
                acc[key] = visible;
                return acc;
            },
            {} as Record<string, boolean>
        );
        this.persist(gridKey, { visibility: this.diffFromDefaults(updated, defaults), order: this.getOrder(gridKey) });
    }

    /** Pass `[]` to go back to the template-declared order. */
    setOrder(gridKey: string, order: string[]) {
        this.persist(gridKey, { visibility: this.resolveSaved(gridKey)?.visibility ?? {}, order });
    }

    resetToDefault(gridKey: string) {
        // Target state IS the defaults - nothing needs to be stored.
        this.persist(gridKey, { visibility: {}, order: [] });
    }

    /** dirty (unsent edit) -> pending buffer (edit never confirmed saved) -> DB; undefined = nothing saved. */
    private resolveSaved(gridKey: string): GridColumnSettings | undefined {
        const unsent = this.dirty.get(gridKey);
        if (unsent) {
            return unsent;
        }

        // An edit from an earlier page load that never got confirmed saved: apply it and finish the save.
        const leftover = this.readPending(gridKey);
        if (leftover) {
            this.dirty.set(gridKey, leftover);
            this.getSaveTrigger(gridKey).next();
            return leftover;
        }

        return this.normalize(currentUserSubject.value?.gridSettings?.[gridKey]) ?? undefined;
    }

    private persist(gridKey: string, settings: GridColumnSettings) {
        this.writePending(gridKey, settings);
        this.dirty.set(gridKey, settings);
        this.getSaveTrigger(gridKey).next();
    }

    private getSaveTrigger(gridKey: string): Subject<void> {
        let trigger = this.saveTriggers.get(gridKey);
        if (!trigger) {
            trigger = new Subject<void>();
            trigger.pipe(debounceTime(SAVE_DEBOUNCE_MS)).subscribe(() => this.sendToBackend(gridKey));
            this.saveTriggers.set(gridKey, trigger);
        }
        return trigger;
    }

    private sendToBackend(gridKey: string) {
        const settings = this.dirty.get(gridKey);
        if (!settings) {
            return;
        }

        this.userService.saveGridSettings(gridKey, settings).subscribe(() => {
            const user = currentUserSubject.value;
            if (user) {
                user.gridSettings = {
                    ...(user.gridSettings || {}),
                    [gridKey]: { visibility: { ...settings.visibility }, order: [...(settings.order ?? [])] }
                };
                currentUserSubject.next(user);
            }
            // Not if the user edited again while this request was in flight - that newer edit is
            // still unsent and has its own save pending.
            if (this.dirty.get(gridKey) === settings) {
                this.dirty.delete(gridKey);
                this.removePending(gridKey);
            }
        });
    }

    /** Best-effort save for any unsent changes, used on tab close/navigation away. */
    private flushPending() {
        if (this.dirty.size === 0) {
            return;
        }

        const token = UserService.getAccessToken();
        this.dirty.forEach((settings, gridKey) => {
            fetch(`${environment.apiUrl}/user/SaveGridSettings`, {
                method: 'POST',
                keepalive: true,
                headers: {
                    'Content-Type': 'application/json',
                    ...(token ? { Authorization: `Bearer ${token}` } : {})
                },
                body: JSON.stringify({ gridKey, settings: settings.visibility, columnOrder: settings.order ?? [] })
            }).catch(() => undefined);
            // The request is in flight and its outcome can't be observed during unload. A leftover
            // buffer entry would override newer DB data saved from another browser on the next load.
            this.removePending(gridKey);
        });
        this.dirty.clear();
    }

    private getDefaults(columnKeys: string[], defaultVisibility: Record<string, boolean>): Record<string, boolean> {
        return columnKeys.reduce(
            (acc, key) => {
                acc[key] = defaultVisibility[key] ?? true;
                return acc;
            },
            {} as Record<string, boolean>
        );
    }

    private mergeKnownSettings(
        columnKeys: string[],
        defaults: Record<string, boolean>,
        settings: Record<string, boolean>
    ): Record<string, boolean> {
        return columnKeys.reduce(
            (acc, key) => {
                acc[key] = settings[key] ?? defaults[key];
                return acc;
            },
            {} as Record<string, boolean>
        );
    }

    private diffFromDefaults(
        settings: Record<string, boolean>,
        defaults: Record<string, boolean>
    ): Record<string, boolean> {
        return Object.keys(settings).reduce(
            (acc, key) => {
                if (settings[key] !== defaults[key]) {
                    acc[key] = settings[key];
                }
                return acc;
            },
            {} as Record<string, boolean>
        );
    }

    private getPendingKey(gridKey: string): string {
        const user = currentUserSubject.value;
        const userScope = user?.id?.toString() || user?.userName || 'anonymous';
        return `${STORAGE_PREFIX}.${gridKey}.${userScope}`;
    }

    private readPending(gridKey: string): GridColumnSettings | null {
        const raw = localStorage.getItem(this.getPendingKey(gridKey));
        if (!raw) {
            return null;
        }

        try {
            return this.normalize(JSON.parse(raw));
        } catch {
            return null;
        }
    }

    /** Accepts the current `{ visibility, order }` shape and the legacy bare `{ columnKey: visible }` map. */
    private normalize(raw: unknown): GridColumnSettings | null {
        if (!raw || typeof raw !== 'object') {
            return null;
        }

        const candidate = raw as Partial<GridColumnSettings>;
        if (candidate.visibility && typeof candidate.visibility === 'object') {
            return {
                visibility: candidate.visibility,
                order: Array.isArray(candidate.order) ? candidate.order : []
            };
        }

        return { visibility: raw as Record<string, boolean>, order: [] };
    }

    /** An empty diff is stored too: a reset that never reached the backend must still be re-sent. */
    private writePending(gridKey: string, settings: GridColumnSettings) {
        localStorage.setItem(this.getPendingKey(gridKey), JSON.stringify(settings));
    }

    private removePending(gridKey: string) {
        localStorage.removeItem(this.getPendingKey(gridKey));
    }

    private removeLegacyCache() {
        const legacyKeys: string[] = [];
        for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i);
            if (key?.startsWith(`${LEGACY_STORAGE_PREFIX}.`)) {
                legacyKeys.push(key);
            }
        }
        legacyKeys.forEach((key) => localStorage.removeItem(key));
    }
}
